import { useMemo } from 'react'
import { useDashboardWidgetData } from 'hooks/useWidgetState'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import { InsightCount } from 'schemas/action/InsightCountSchema'
import { DashboardWidget } from 'features/widget/types'
import { WidgetId } from 'features/types'
import { InsightData } from 'schemas/action/InsightDataSchema'
import CountType from 'enums/CountType'
import ViewOption from 'enums/InsightViewOption'

import { TagsWidgetsType, ExtendedInsightData } from './types'

const generateMaps = (insightDatas: InsightData[]) => {
  const insightDataMap = new Map<string, ExtendedInsightData>()
  let minLevel: number | undefined
  let maxValue = 0
  insightDatas.forEach((insight) => {
    if (minLevel === undefined || minLevel > insight.level!) {
      minLevel = insight.level!
    }

    if (maxValue === undefined || maxValue < insight.value!) {
      maxValue = insight.value!
    }

    // * generate insightDataMap
    insightDataMap.set(insight.searchName!, {
      ...insight,
      parent: insight.searchName?.substring(
        0,
        insight.searchName.length - insight!.name!.length - 1
      )
    })
  })

  // * after generating insightDataMap,
  // * adding nextLevelChildren and subRows info into insightDataMap
  //* (need to add by level (higest level to lowest level))
  const insightDataArrs = Array.from(insightDataMap.values()).sort((a, b) => b.level! - a.level!)
  insightDataArrs.forEach((insight) => {
    const nextLevelChildren = insight.children?.filter((child) => {
      const childInsight = insightDataMap.get(child!)
      return childInsight?.parent === insight.searchName
    }) as string[]

    const subRows = nextLevelChildren.map(
      (child) => insightDataMap.get(child!) as ExtendedInsightData
    )
    insightDataMap.set(insight.searchName!, {
      ...insight,
      nextLevelChildren,
      subRows
    })
  })

  // * generating tableData
  const minLevelData = insightDatas.filter((insight) => insight.level === minLevel)
  const tableData = minLevelData.map((insight) =>
    insightDataMap.get(insight.searchName!)
  ) as ExtendedInsightData[]
  return { insightDataMap, tableData, maxValue }
}

const getTagsData = (widgetData: DashboardWidget<InsightCount>, targetLevel?: number) => {
  const resultData = widgetData.data![0]!.results![0]!.data as InsightCount
  const insightDatas = resultData!.insights![0]!.dataset![0].set as InsightData[]

  return generateMaps(
    targetLevel === undefined
      ? insightDatas
      : insightDatas.filter((insight) => insight.level === targetLevel)
  )
}

const useTagsData = (widgetId: WidgetId) => {
  const widgetData = useDashboardWidgetData<InsightCount>(widgetId)
  const widgetSetting = useDashboardWidgetSetting<TagsWidgetsType>(widgetId)

  const tagsData = useMemo(
    () => ({
      ...getTagsData(widgetData, widgetSetting.level),
      countType: widgetSetting.countType || CountType.POSTS,
      showStarRating: widgetSetting.showStarRating || false,
      showSurveyScore: widgetSetting.showSurveyScore || false,
      viewOption: widgetSetting.viewOption || ViewOption.TAGS,
      expandableTableSortOptions: widgetSetting.expandableTableSortOptions ?? []
    }),
    [widgetData, widgetSetting]
  )

  return tagsData
}

export default useTagsData
